import type { CmsPage } from '../../cms/types'
import { normalizeCategory } from '../normalizers'
import {
  buildCanonicalPathFromCmsSlug,
  buildDefaultSeoAlternates,
  buildSeoAlternatesFromRawTranslations,
  dedupeSeoAlternates,
} from '../seo/localePolicy'
import { fetchJson } from '../ssr'
import { type ShopLocale } from '../../i18n/shopLocale'

export const fetchNormalizedCategories = async (apiBase: string) => {
  const query = new URLSearchParams({
    page: '1',
    limit: '100',
    sortBy: 'order',
    sortOrder: 'asc',
  })
  const categoriesResponse = await fetchJson(`${apiBase}/catalog/categories?${query.toString()}`)
  return Array.isArray(categoriesResponse.data?.categories)
    ? (categoriesResponse.data.categories as Record<string, unknown>[])
        .map((entry) => normalizeCategory(entry))
        .filter((entry) => entry._id && entry.slug)
    : []
}

export const fetchPageForLocale = async ({
  apiBase,
  pageIdOrSlug,
  locale,
}: {
  apiBase: string
  pageIdOrSlug: string
  locale: ShopLocale
}) =>
  fetchJson(
    pageIdOrSlug === '/'
      ? `${apiBase}/pages?homepage=1&locale=${encodeURIComponent(locale)}`
      : `${apiBase}/pages/${encodeURIComponent(pageIdOrSlug)}?locale=${encodeURIComponent(locale)}`,
  )

export const enrichPageSeoLocaleData = async ({
  apiBase,
  page,
  fallbackPathResolver,
}: {
  apiBase: string
  page: CmsPage
  fallbackPathResolver?: (slug: string) => string
}) => {
  const pageIdentifier = page._id || page.slug
  const canonicalPathResolver = fallbackPathResolver ?? buildCanonicalPathFromCmsSlug
  const fallbackPath = canonicalPathResolver(page.slug)
  const fallbackAlternates = buildDefaultSeoAlternates(fallbackPath)
  const currentPageAlternate = [{ locale: page.locale, path: fallbackPath }]
  if (!pageIdentifier) {
    page.seoAlternates = dedupeSeoAlternates(currentPageAlternate, fallbackPath)
    return
  }
  const translationsResponse = await fetchJson(
    `${apiBase}/pages/${encodeURIComponent(pageIdentifier)}/translations?locale=${encodeURIComponent(page.locale)}`,
  )
  if (!translationsResponse.ok) {
    page.seoAlternates = dedupeSeoAlternates(currentPageAlternate, fallbackPath)
    return
  }

  const fromTranslations = buildSeoAlternatesFromRawTranslations(translationsResponse.data?.translations, (slug) =>
    canonicalPathResolver(slug),
  )
  const alternates = dedupeSeoAlternates(
    [...fromTranslations, ...currentPageAlternate],
    fallbackPath,
  )
  page.seoAlternates = alternates.length ? alternates : fallbackAlternates
}
